import { find } from 'lodash'

function photoPublicUrl(photo) {
  if (!photo) return null
  const url = photo.publicUrl || photo.imageUrl || photo.url || photo.fileUrl
  return url ? String(url) : null
}

function resolvePhotoFromPool(photos) {
  if (!Array.isArray(photos) || !photos.length) return null
  const avatar = find(photos, { isAvatar: true }) || photos[0]
  return photoPublicUrl(avatar)
}

/**
 * Resolve creatives for one BO series document.
 * New model: firstPhotos + secondPhotos (two banners per series).
 * Legacy: desktopPhotos / tablet / mobile single-or-multi list.
 */
export function resolveSeriesImageUrls(series) {
  if (!series || typeof series !== 'object') return []

  const hasSeriesSlots =
    (Array.isArray(series.firstPhotos) && series.firstPhotos.length > 0) ||
    (Array.isArray(series.secondPhotos) && series.secondPhotos.length > 0)

  if (hasSeriesSlots) {
    const urls = []
    const first = resolvePhotoFromPool(series.firstPhotos)
    const second = resolvePhotoFromPool(series.secondPhotos)
    if (first) urls.push(first)
    if (second) urls.push(second)
    return urls
  }

  const pools = [series.desktopPhotos, series.tabletPhotos, series.mobilePhotos, series.photos]
  for (const photos of pools) {
    if (!Array.isArray(photos) || !photos.length) continue
    // Legacy multi-image desktopPhotos without first/second: one tile per photo with url.
    const urls = photos.map(photoPublicUrl).filter(Boolean)
    if (urls.length) return urls
  }

  return []
}

/** @deprecated use resolveSeriesImageUrls */
export function resolveBannerImage(banner) {
  const urls = resolveSeriesImageUrls(banner)
  return urls[0] || null
}

/**
 * Map Firestore series docs to Hero grid tiles (image + optional link only).
 * Each series contributes up to two tiles (banner 1, banner 2), preserving series pos order.
 */
export function mapBoBannersToHeroTiles(banners) {
  if (!Array.isArray(banners)) return []

  const tiles = []
  banners.forEach((series) => {
    const urls = resolveSeriesImageUrls(series)
    const href = series.link ? String(series.link).trim() : ''
    const baseAlt = series.name || ''
    urls.forEach((imageSrc, index) => {
      tiles.push({
        id: `${series.id}-${index + 1}`,
        seriesId: series.id,
        slot: index + 1,
        imageSrc,
        imageAlt: urls.length > 1 ? `${baseAlt} (${index + 1})` : baseAlt,
        href,
      })
    })
  })
  return tiles
}

/**
 * Mobile (1-up): within each series show banner 2, then banner 1.
 * Series order (pos) stays the same. Desktop/tablet keep BO order 1 → 2.
 */
export function reorderHeroSeriesForMobile(tiles) {
  if (!Array.isArray(tiles) || tiles.length < 2) return tiles || []

  const result = []
  let i = 0
  while (i < tiles.length) {
    const tile = tiles[i]
    const next = tiles[i + 1]
    if (
      next &&
      tile.seriesId &&
      tile.seriesId === next.seriesId &&
      tile.slot === 1 &&
      next.slot === 2
    ) {
      result.push(next, tile)
      i += 2
    } else {
      result.push(tile)
      i += 1
    }
  }
  return result
}
